import React, { useMemo } from 'react'
import { Formik, Form, Field, FieldArray, FormikErrors } from 'formik'
import { IntlShape } from 'react-intl'
import * as Yup from 'yup'
import { useQuery } from 'react-query'
import { getAll } from '../../../../../../services/common_requests'
import { SeasonalPrice, TimeSlot } from './SeasonalPricing'
import OverlayTrigger from 'react-bootstrap/OverlayTrigger'
import Tooltip from 'react-bootstrap/Tooltip'
import { PersonTypePriceRow } from './PersonTypePriceRow'

interface PersonType {
  _id: string
  name: string
  code: string
  description: string
  defaultPriceMultiplier: number
  isActive: boolean
  minAge: number
  maxAge: number
}

interface PersonTypePrice {
  personTypeId: string
  price: number
  priceHT: number
}

interface FormValues extends Omit<SeasonalPrice, 'personTypePrices' | 'timeSlots'> {
  personTypePrices: PersonTypePrice[]
  timeSlots: TimeSlot[]
}

interface SeasonalPricingFormProps {
  initialValues: SeasonalPrice
  onSubmit: (values: SeasonalPrice) => void
  onCancel: () => void
  intl: IntlShape
  tourId: string
  isSubmitting?: boolean
}

const today = new Date();
today.setHours(0,0,0,0);
const validationSchema = Yup.object().shape({
  priceTTC: Yup.number()
    .required('Required')
    .min(0.01, 'Price must be greater than 0'),
  priceHT: Yup.number()
    .required('Required')
    .min(0.01, 'Price must be greater than 0'),
  startDate: Yup.date()
    .min(today, 'Start date cannot be in the past')
    .required('Start date is required'),
  endDate: Yup.date()
    .min(today, 'End date cannot be in the past')
    .when('startDate', (startDate, schema) =>
      startDate
        ? schema.min(startDate, 'End date must be after start date')
        : schema
    )
    .required('End date is required'),
  priority: Yup.number()
    .required('Priority is required')
    .min(0, 'Priority must be a positive number'),
  pricingMode: Yup.string().required('Required'),
  personTypePrices: Yup.array().when('pricingMode', {
    is: 'PER_PERSON',
    then: Yup.array().of(
      Yup.object().shape({
        personTypeId: Yup.string().required('Required'),
        price: Yup.number()
          .required('Required')
          .min(0.01, 'Price must be greater than 0'),
        priceHT: Yup.number()
          .required('Required')
          .min(0.01, 'Price must be greater than 0'),
      })
    ),
    otherwise: Yup.array(),
  })
})

function getNextAvailablePersonType(personTypes: PersonType[], selectedIds: string[]): PersonType | undefined {
  return personTypes.find(pt => !selectedIds.includes(pt._id));
}

const SeasonalPricingForm: React.FC<SeasonalPricingFormProps> = ({
  initialValues,
  onSubmit,
  onCancel,
  intl,
  tourId,
  isSubmitting,
}) => {
  // Fetch person types
  const { data: personTypesData } = useQuery(
    'personTypes',
    () => getAll(null, '/person-types'),
    {
      staleTime: Infinity,
    }
  )

  const personTypes = useMemo(() => (personTypesData?.data || []) as PersonType[], [personTypesData])

  const calculatePriceHT = (priceTTC: number) => {
    // Assuming VAT rate of 10% - adjust this according to your business logic
    return Number((priceTTC / 1.1).toFixed(2))
  }

  // Ensure initial dates are properly formatted
  const formattedInitialValues = {
    ...initialValues,
    startDate: initialValues.startDate || new Date().toISOString().split('T')[0],
    endDate: initialValues.endDate || new Date().toISOString().split('T')[0],
    personTypePrices: initialValues.personTypePrices?.length 
      ? initialValues.personTypePrices 
      : [{ personTypeId: '', price: 0, priceHT: 0 }],
    timeSlots: initialValues.timeSlots || [],
    priceHT: initialValues.priceHT || calculatePriceHT(initialValues.priceTTC || 0),
    pricingMode: initialValues.pricingMode || 'PER_GROUP',
  }

  const allSelected = useMemo(() => personTypes.length === formattedInitialValues.personTypePrices.length, [personTypes, formattedInitialValues.personTypePrices])

  return (
    <Formik<FormValues>
      initialValues={formattedInitialValues}
      validationSchema={validationSchema}
      onSubmit={onSubmit as any}
    >
      {({ values, errors, touched, setFieldValue }) => {
        // Type casting for proper TypeScript typing
        const typedErrors = errors as FormikErrors<FormValues>;
        const typedTouched = touched as { [K in keyof FormValues]?: boolean };
        
        return (
          <Form className='form'>
            <div className='row mb-4'>
              <div className='col-md-6'>
                <label className='form-label required'>
                  {intl.formatMessage({ id: 'COLUMNS.PRICE' })}
                </label>
                <Field
                  type='number'
                  name='priceTTC'
                  min='0.1'
                  step='0.1'
                  className={`form-control form-control-lg ${
                    typedErrors.priceTTC && typedTouched.priceTTC ? 'is-invalid' : ''
                  }`}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const priceTTC = parseFloat(e.target.value)
                    setFieldValue('priceTTC', priceTTC)
                    setFieldValue('priceHT', calculatePriceHT(priceTTC))
                  }}
                />
                {typedErrors.priceTTC && typedTouched.priceTTC && (
                  <div className='invalid-feedback'>{typedErrors.priceTTC as string}</div>
                )}
              </div>
            </div>

            <div className='row mb-4'>
              <div className='col-md-6'>
                <label className='form-label required'>
                  {intl.formatMessage({ id: 'TOUR.PRICING.SEASONAL.START_DATE' })}
                </label>
                <Field
                  type='date'
                  name='startDate'
                  min={new Date().toISOString().split('T')[0]}
                  className={`form-control form-control-lg ${
                    typedErrors.startDate && typedTouched.startDate ? 'is-invalid' : ''
                  }`}
                />
                {typedErrors.startDate && typedTouched.startDate && (
                  <div className='invalid-feedback'>{typedErrors.startDate as string}</div>
                )}
              </div>

              <div className='col-md-6'>
                <label className='form-label required'>
                  {intl.formatMessage({ id: 'TOUR.PRICING.SEASONAL.END_DATE' })}
                </label>
                <Field
                  type='date'
                  name='endDate'
                  min={values.startDate || new Date().toISOString().split('T')[0]}
                  className={`form-control form-control-lg ${
                    typedErrors.endDate && typedTouched.endDate ? 'is-invalid' : ''
                  }`}
                />
                {typedErrors.endDate && typedTouched.endDate && (
                  <div className='invalid-feedback'>{typedErrors.endDate as string}</div>
                )}
              </div>
            </div>

            <div className='d-flex justify-content-between align-items-center mb-3'>
              <h4 className='fw-bold mb-0'>
                {intl.formatMessage({ id: 'TOUR.PRICING.PERSON_TYPE_PRICES' })}
              </h4>
              <div className='form-check form-switch'>
                <input
                  className='form-check-input form-check-input-lg'
                  type='checkbox'
                  role='switch'
                  id='pricingMode'
                  checked={values.pricingMode === 'PER_PERSON'}
                  onChange={(e) => {
                    const isPerPerson = e.target.checked;
                    setFieldValue('pricingMode', isPerPerson ? 'PER_PERSON' : 'PER_GROUP');
                    if (!isPerPerson) {
                      setFieldValue('personTypePrices', []);
                    } else {
                      if (personTypes.length > 0) {
                        if (values.personTypePrices.length === 0) {
                          setFieldValue('personTypePrices', [{
                            personTypeId: personTypes[0]._id,
                            price: 0,
                            priceHT: 0,
                          }]);
                        } else if (values.personTypePrices[0].personTypeId === '') {
                          const updated = [...values.personTypePrices];
                          updated[0].personTypeId = personTypes[0]._id;
                          setFieldValue('personTypePrices', updated);
                        }
                      }
                    }
                  }}
                />
                <label className='form-check-label ms-2' htmlFor='pricingMode'>
                  {values.pricingMode === 'PER_PERSON'
                    ? intl.formatMessage({ id: 'TOUR.PRICING.PER_PERSON' })
                    : intl.formatMessage({ id: 'TOUR.PRICING.PER_GROUP' })}
                </label>
              </div>
            </div>
            {values.pricingMode !== 'PER_PERSON' && (
              <div className='text-center text-muted mb-4'>
                {intl.formatMessage({ id: 'TOUR.PRICING.GROUP_PRICING_MODE', defaultMessage: 'Currently using group pricing mode. Enable person type pricing to set different prices for age groups.' })}
              </div>
            )}
            {values.pricingMode === 'PER_PERSON' && (
              <div className='mb-4'>
                <FieldArray name='personTypePrices'>
                  {(arrayHelpers: { push: any; remove: any }) => {
                    const { push, remove } = arrayHelpers;
                    return (
                    <>
                        {values.personTypePrices.map((_, index) => (
                          <PersonTypePriceRow
                            key={index}
                            index={index}
                            typedErrors={typedErrors}
                            typedTouched={typedTouched}
                            personTypes={personTypes}
                            setFieldValue={setFieldValue}
                            intl={intl}
                            remove={remove}
                            values={values}
                            calculatePriceHT={calculatePriceHT}
                            allSelected={allSelected}
                          />
                        ))}

                        <div className='d-flex justify-content-end'>
                          <OverlayTrigger
                            placement="top"
                            overlay={
                              allSelected ? (
                                <Tooltip id="add-disabled-tooltip">
                                  {intl.formatMessage({ id: 'TOUR.PRICING.ALL_PERSON_TYPES_USED', defaultMessage: 'All person types have been added' })}
                                </Tooltip>
                              ) : <></>
                            }
                          >
                            <span>
                        <button
                          type='button'
                          className='btn btn-sm btn-light'
                                onClick={() => {
                                  const selectedIds = values.personTypePrices.map(p => p.personTypeId);
                                  const nextPersonType = getNextAvailablePersonType(personTypes, selectedIds);
                                  if (nextPersonType) {
                            push({
                                      personTypeId: nextPersonType._id,
                              price: 0,
                              priceHT: 0,
                                    });
                          }
                                }}
                                disabled={allSelected}
                        >
                          {intl.formatMessage({ id: 'COMMON.ADD' })}
                        </button>
                            </span>
                          </OverlayTrigger>
                      </div>
                    </>
                    )
                  }}
                </FieldArray>
              </div>
            )}

            <div className='d-flex justify-content-end gap-2 mt-5'>
              <button
                type='button'
                className='btn btn-light'
                onClick={onCancel}
                disabled={isSubmitting}
              >
                {intl.formatMessage({ id: 'TOUR.ACTIONS.CANCEL' })}
              </button>
              <button
                type='submit'
                className='btn btn-primary'
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <span className='spinner-border spinner-border-sm me-2' role='status' aria-hidden='true'></span>
                ) : null}
                {intl.formatMessage({ id: 'TOUR.ACTIONS.SAVE' })}
              </button>
            </div>
          </Form>
        )
      }}
    </Formik>
  )
}

export default SeasonalPricingForm 